// In-app help: which screens have a guide, and the guides themselves.
// Guides come from the admin panel's Documentation module (api/inhouse_app/docs/*) and are
// cached on the phone by version, so they still open in the field with no signal.
import axios from 'axios';

const INDEX_KEY = 'docs_index_v1';
const DOC_PREFIX = 'docs_doc_v1_';
const SEEN_PREFIX = 'docs_seen_v1_';
const INDEX_MAX_AGE_MS = 10 * 60 * 1000;
// Screenshots in a guide come as signed links valid for 7 days, so a saved copy older than
// 6 days is downloaded again (when online) even if the guide's version has not changed.
const DOC_MAX_AGE_MS = 6 * 24 * 60 * 60 * 1000;

export interface DocSummary {
  slug: string;
  title: string;
  category: string;
  summary: string | null;
  version: number;
  change_summary: string | null;
  updated_at: string | null;
}

export interface DocIndex {
  fetchedAt: number;
  // Vue Router route pattern (e.g. "/sales-leads/:leadId") -> guide
  screens: Record<string, { slug: string; anchor: string | null }>;
  docs: DocSummary[];
}

export interface DocFull extends DocSummary {
  // When this copy was downloaded (ms), to know when its signed image links need renewing.
  saved_at?: number;
  html: string;
  toc: { level: number; id: string; text: string }[];
  changelog: { version: number; change_summary: string; date: string }[];
}

function readJson<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch (e) {
    return null;
  }
}

function credentials() {
  return { username: window.localStorage.username, password: window.localStorage.password };
}

export function cachedIndex(): DocIndex | null {
  return readJson<DocIndex>(INDEX_KEY);
}

/** Refresh the list of guides (at most every 10 minutes unless forced). Never throws. */
export async function refreshDocsIndex(force = false): Promise<DocIndex | null> {
  const cached = cachedIndex();
  if (!window.localStorage.username || !window.localStorage.password) {
    return cached;
  }
  if (!force && cached && Date.now() - cached.fetchedAt < INDEX_MAX_AGE_MS) {
    return cached;
  }
  try {
    const response = await axios.post('docs/index', credentials(), { skipLoading: true } as any);
    if (response.data && response.data.status) {
      const index: DocIndex = { fetchedAt: Date.now(), screens: response.data.screens || {}, docs: response.data.docs || [] };
      window.localStorage.setItem(INDEX_KEY, JSON.stringify(index));
      return index;
    }
  } catch (e) {
    // Offline or server error: keep using the cached copy.
  }
  return cached;
}

/** The guide for a route pattern, with its current version, or null if the screen has none. */
export function docForRoute(pattern: string | undefined): { slug: string; anchor: string | null; doc: DocSummary | null } | null {
  const index = cachedIndex();
  if (!index || !pattern) {
    return null;
  }
  const entry = index.screens[pattern];
  if (!entry) {
    return null;
  }
  return { ...entry, doc: index.docs.find(d => d.slug === entry.slug) || null };
}

/** Fetch a guide, falling back to the phone's copy when offline. */
export async function getDoc(slug: string): Promise<{ doc: DocFull | null; offline: boolean }> {
  const cached = readJson<DocFull>(DOC_PREFIX + slug);
  const known = (cachedIndex()?.docs || []).find(d => d.slug === slug);
  // Same version and recent enough that its image links are still valid: no network needed.
  const fresh = cached && cached.saved_at && Date.now() - cached.saved_at < DOC_MAX_AGE_MS;
  if (cached && known && cached.version === known.version && fresh) {
    return { doc: cached, offline: false };
  }
  try {
    const response = await axios.post('docs/get', { ...credentials(), slug }, { skipLoading: true } as any);
    if (response.data && response.data.status) {
      const doc: DocFull = { ...response.data.doc, saved_at: Date.now() };
      window.localStorage.setItem(DOC_PREFIX + slug, JSON.stringify(doc));
      return { doc, offline: false };
    }
  } catch (e) {
    // fall through to the cached copy
  }
  return { doc: cached, offline: cached !== null };
}

/** True when the guide changed since the user last opened it (drives the "Guide updated" badge). */
export function isUpdatedSinceSeen(slug: string, version: number | undefined): boolean {
  if (!version) {
    return false;
  }
  const seen = Number(window.localStorage.getItem(SEEN_PREFIX + slug) || 0);
  return seen > 0 && seen < version;
}

export function lastSeenVersion(slug: string): number {
  return Number(window.localStorage.getItem(SEEN_PREFIX + slug) || 0);
}

export function markSeen(slug: string, version: number): void {
  window.localStorage.setItem(SEEN_PREFIX + slug, String(version));
}
